import {
  ACTIONS,
  actionNames,
  type ActionName,
  type AgentCapability,
  type ResourcePath,
} from '@occ/contracts';
import { useCan } from '@refinedev/core';
import { useState } from 'react';
import { requiredCapability } from '../auth/rbac';
import { CARD_ACTIONS, runAction, surfaceOf, type NodePanelId } from '../data/actions';
import { openScreenSession } from '../data/screen';
import { ErrorNotice } from './ErrorNotice';
import { Badge, Button, Card, ConfirmDialog } from './ui';

/**
 * Botones de acción de la ficha de nodo (Módulo 8).
 *
 * Se generan recorriendo el catálogo de `@occ/contracts`, no con una lista
 * escrita a mano: añadir una capacidad al agente la hace aparecer aquí y como
 * herramienta MCP a la vez (P3). Un botón puede estar apagado o llevar a otro
 * sitio por cuatro razones distintas, y se distinguen porque llevan a cuatro
 * acciones distintas del operador:
 *
 * - **sin permiso** — el RBAC del usuario no alcanza (lo confirmará el backend);
 * - **no soportado** — el agente de *ese* nodo no declaró la capacidad, así que
 *   hay que actualizarlo;
 * - **desconectado** — el nodo no tiene túnel abierto, hay que encenderlo;
 * - **necesita datos** — la acción existe y se puede, pero hay que decirle qué
 *   servicio, qué ruta o qué script: el botón lleva a la pestaña que lo pide.
 *
 * Una acción nueva en el catálogo que el panel todavía no sepa pedir sale
 * igualmente, apagada y diciéndolo. Desaparecer sería peor: nadie se enteraría de
 * que el panel va por detrás del contrato.
 */

type Props = {
  nodeId: string;
  alias: string;
  online: boolean;
  capabilities: AgentCapability[];
  path: ResourcePath;
  /** Lleva a la pestaña que sabe pedir los datos que le faltan a la acción. */
  onOpenPanel: (panel: NodePanelId) => void;
  /**
   * El nodo se dio de baja y ya no existe. Quien hospeda esta ficha tiene que
   * sacar al operador de ella: seguir en la ficha de un nodo borrado daría 404 en
   * la siguiente lectura.
   */
  onDecommissioned?: () => void;
};

type ActionState =
  | { phase: 'idle' }
  | { phase: 'running'; action: ActionName }
  | { phase: 'done'; action: ActionName; output: unknown }
  | { phase: 'failed'; action: ActionName; error: unknown };

const NODE_ACTIONS = actionNames.filter(
  (name) => ACTIONS[name].scope === 'node' && !CARD_ACTIONS.has(name),
);

/** Nombre de la pestaña, para poder decir «se hace en Archivos» y no «en archivos». */
const PANEL_LABEL: Record<NodePanelId, string> = {
  terminal: 'Terminal',
  archivos: 'Archivos',
  scripts: 'Scripts',
  servicios: 'Servicios',
};

export function NodeActions({
  nodeId,
  alias,
  online,
  capabilities,
  path,
  onOpenPanel,
  onDecommissioned,
}: Props) {
  const [state, setState] = useState<ActionState>({ phase: 'idle' });
  const [confirming, setConfirming] = useState<ActionName | null>(null);

  const execute = async (name: ActionName) => {
    setConfirming(null);
    setState({ phase: 'running', action: name });
    try {
      // `name` es un `ActionName` genérico en este punto, así que TypeScript no
      // puede estrechar la entrada a la del catálogo. Solo se ejecutan aquí las
      // acciones de un clic, cuya única entrada es la referencia al nodo.
      const output = await runAction(name, { node: { nodeId } } as never);
      setState({ phase: 'done', action: name, output });
    } catch (error) {
      setState({ phase: 'failed', action: name, error });
    }
  };

  // `screen.open` no pasa por `execute`: necesita abrir la pestaña del lanzador
  // y entregarle el vale por `postMessage` (ver `data/screen.ts`), algo que el
  // camino genérico —pensado para acciones de una sola llamada— no hace.
  const executeScreenOpen = async () => {
    setConfirming(null);
    setState({ phase: 'running', action: 'screen.open' });
    try {
      const handle = await openScreenSession(nodeId);
      setState({ phase: 'done', action: 'screen.open', output: handle });
    } catch (error) {
      setState({ phase: 'failed', action: 'screen.open', error });
    }
  };

  // La baja del nodo no cabe en `execute`: su entrada no es solo la referencia al
  // nodo, lleva el alias que el operador acaba de escribir para confirmar.
  const executeDecommission = async () => {
    setConfirming(null);
    setState({ phase: 'running', action: 'nodes.decommission' });
    try {
      // Se manda el alias del nodo, no lo que se teclee: el diálogo no deja llegar
      // aquí hasta que lo tecleado coincide, así que son el mismo valor y este es
      // el que no depende de que el campo siga montado.
      const output = await runAction('nodes.decommission', {
        node: { nodeId },
        confirmAlias: alias,
      });
      setState({ phase: 'done', action: 'nodes.decommission', output });
      onDecommissioned?.();
    } catch (error) {
      setState({ phase: 'failed', action: 'nodes.decommission', error });
    }
  };

  const pideAlias = confirming !== null && surfaceOf(confirming)?.kind === 'confirm-alias';

  const launch = (name: ActionName) => {
    const surface = surfaceOf(name);
    if (surface?.kind === 'panel') {
      onOpenPanel(surface.panel);
      return;
    }
    if (name === 'screen.open') {
      void executeScreenOpen();
      return;
    }
    if (ACTIONS[name].destructive) {
      setConfirming(name);
      return;
    }
    void execute(name);
  };

  return (
    <Card title="Acciones">
      <div className="flex flex-wrap gap-2">
        {NODE_ACTIONS.map((name) => (
          <ActionButton
            key={name}
            name={name}
            path={path}
            online={online}
            capabilities={capabilities}
            running={state.phase === 'running' && state.action === name}
            onRun={() => launch(name)}
          />
        ))}
      </div>

      {state.phase === 'failed' && (
        <div className="mt-4">
          <ErrorNotice error={state.error} what={state.action} />
        </div>
      )}

      {state.phase === 'done' && (
        <div className="mt-4 grid gap-2">
          <div className="text-xs uppercase tracking-wide opacity-50">
            Resultado de {state.action}
          </div>
          <ActionOutput output={state.output} />
        </div>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={confirming ?? ''}
        confirmLabel={pideAlias ? 'Dar de baja' : 'Ejecutar'}
        // El propio diálogo exige teclear el alias y no deja confirmar hasta que
        // coincida. El backend lo comprueba igual —es él quien decide (P2)— pero
        // dejar pulsar para que conteste 400 haría que el operador leyera un error
        // en vez de ver que se equivocó al escribir.
        confirmText={pideAlias ? alias : undefined}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (!confirming) return;
          if (pideAlias) {
            void executeDecommission();
            return;
          }
          void execute(confirming);
        }}
      >
        <p>{confirming && ACTIONS[confirming].description}</p>
        <p>
          Nodo: <strong>{alias}</strong>
        </p>
        {pideAlias && (
          <p>
            Se desinstalará el agente de la máquina, se revocará su identidad y el nodo desaparecerá
            del OCC. <strong>No se puede deshacer.</strong>
            {!online && ' El nodo está apagado: se desinstalará cuando vuelva a conectarse.'}
          </p>
        )}
      </ConfirmDialog>
    </Card>
  );
}

function ActionButton({
  name,
  path,
  online,
  capabilities,
  running,
  onRun,
}: {
  name: ActionName;
  path: ResourcePath;
  online: boolean;
  capabilities: AgentCapability[];
  running: boolean;
  onRun: () => void;
}) {
  const definition = ACTIONS[name];
  const { data: permission } = useCan({ resource: 'actions', action: name, params: { path } });

  const capability = requiredCapability(name);
  // Lista vacía significa "el backend no las publica todavía", no "el agente no
  // sabe hacer nada": en ese caso no se filtra y decide la puerta.
  const unsupported =
    capabilities.length > 0 && capability !== null && !capabilities.includes(capability);
  const surface = surfaceOf(name);

  // Una baja se hace con el nodo apagado, que es su caso normal: una máquina que
  // se retira ya no está encendida. Exigir conexión obligaría a encenderla para
  // poder darla de baja. Ver `confirm-alias` en `data/actions.ts`.
  const necesitaConexion = surface?.kind !== 'confirm-alias';

  const reason = !permission?.can
    ? (permission?.reason ?? 'Sin permiso')
    : unsupported
      ? `El agente de este nodo no declara la capacidad «${capability}»`
      : necesitaConexion && !online
        ? 'El nodo no está conectado'
        : surface === null
          ? 'El catálogo la ofrece pero el panel todavía no tiene pantalla para pedirle los datos'
          : surface.kind === 'panel'
            ? `${surface.verb} en la pestaña ${PANEL_LABEL[surface.panel]}`
            : definition.description;

  const disabled =
    running || !permission?.can || unsupported || (necesitaConexion && !online) || surface === null;

  return (
    <Button
      variant={definition.destructive ? 'danger' : 'secondary'}
      disabled={disabled}
      title={reason}
      onClick={onRun}
    >
      {running ? '…' : name}
      {definition.destructive && <Badge tone="warn">riesgo</Badge>}
      {surface?.kind === 'panel' && (
        <span aria-hidden className="opacity-50">
          ↗
        </span>
      )}
    </Button>
  );
}

/** Salidas conocidas se pintan; el resto cae al JSON, que siempre es mejor que nada. */
function ActionOutput({ output }: { output: unknown }) {
  const record = (typeof output === 'object' && output !== null ? output : {}) as Record<
    string,
    unknown
  >;

  // La baja dice tres cosas distintas y llevan a tres acciones distintas del
  // operador: nada que hacer, esperar, o ir a la máquina. Un JSON crudo aquí
  // dejaría el tercer caso sin que nadie se enterara.
  if (typeof record.uninstall === 'string' && typeof record.alias === 'string') {
    const mensaje =
      record.uninstall === 'done'
        ? `El nodo ${record.alias} se dio de baja y su agente se está quitando de la máquina.`
        : record.uninstall === 'pending'
          ? `El nodo ${record.alias} se dio de baja. La máquina no estaba conectada: el agente se ` +
            'desinstalará solo en cuanto vuelva a encenderse.'
          : `El nodo ${record.alias} se dio de baja, pero su agente es anterior a la ` +
            'desinstalación remota. Hay que quitarlo en la máquina con «install.ps1 -Uninstall».';
    return (
      <p className="text-sm">
        {mensaje}
        {record.uninstall === 'unsupported' && (
          <>
            {' '}
            <Badge tone="warn">requiere ir a la máquina</Badge>
          </>
        )}
      </p>
    );
  }

  if (typeof record.launchUrl === 'string') {
    if (record.blocked === true) {
      return (
        <p className="text-sm">
          El navegador bloqueó la pestaña emergente.{' '}
          {/* `rel="opener"` a propósito: sin el opener, la pestaña que abra este
              enlace no puede recibir el vale por `postMessage` (ver `data/screen.ts`). */}
          <a className="underline" href={record.launchUrl} target="_blank" rel="opener">
            Abrir la sesión de pantalla
          </a>
        </p>
      );
    }
    return (
      <p className="text-sm">
        Sesión de pantalla abierta en otra pestaña. RustDesk debería arrancar solo, con la
        contraseña ya copiada al portapapeles.
      </p>
    );
  }

  return (
    <pre className="max-h-64 overflow-auto rounded bg-black/5 dark:bg-white/5 p-3 text-xs">
      {JSON.stringify(output, null, 2)}
    </pre>
  );
}
